import React, { useContext, useMemo } from 'react'
import { ThemeContext } from 'styled-components'
import { Pair, ZERO_ADDRESS } from '@uniswap/sdk'
import { Link } from 'react-router-dom'
import { SwapPoolTabs } from '../../components/NavigationTabs'

import Question from '../../components/QuestionHelper'
import FullPositionCard from '../../components/PositionCard'
import { useTokenBalancesWithLoadingIndicator } from '../../state/wallet/hooks'
import { StyledInternalLink, TYPE } from '../../theme'
import { Text } from 'rebass'
import { LightCard } from '../../components/Card'
import { RowBetween } from '../../components/Row'
import { ButtonPrimary, ButtonSecondary } from '../../components/Button'
import { AutoColumn } from '../../components/Column'

import { useActiveWeb3React } from '../../hooks'
import { usePairs } from '../../data-mooniswap/Reserves'
import { useTrackedTokenPairs } from '../../state/user/hooks'
import AppBody from '../AppBody'
import { Dots } from '../../components/swap/styleds'
import ReferralLink from '../../components/RefferalLink'

export default function Pool() {
  const theme = useContext(ThemeContext)
  const { account } = useActiveWeb3React()

  const trackedTokenPairs = useTrackedTokenPairs()
  const pairs = usePairs(trackedTokenPairs)

  const tokenPairsWithLiquidityTokens = pairs.map(([state, pair]) => {
    if (!pair) {
      return undefined
    }
    return {
      liquidityToken: pair?.liquidityToken
    }
  })
  const liquidityTokens = useMemo(() => tokenPairsWithLiquidityTokens.map(tpwlt => tpwlt?.liquidityToken), [
    tokenPairsWithLiquidityTokens
  ])

  const [v2PairsBalances, fetchingV2PairBalances] = useTokenBalancesWithLoadingIndicator(
    account ?? undefined,
    liquidityTokens
  )

  // fetch the reserves for all V2 pools in which the user has a balance
  const liquidityTokensWithBalances = useMemo(
    () =>
      tokenPairsWithLiquidityTokens.filter((data) => {
        if (!data) {
          return false
        }
        return v2PairsBalances[data.liquidityToken.address]?.greaterThan('0')
      }),
    [tokenPairsWithLiquidityTokens, v2PairsBalances]
  )

  const v2IsLoading =
    fetchingV2PairBalances || pairs?.length < liquidityTokensWithBalances.length || pairs?.some(pair => !pair)

  const allV2PairsWithLiquidity = pairs.map(([, pair]) => pair).filter((pair): pair is Pair => {
    if (!pair) {
      return false
    }
    return liquidityTokensWithBalances.findIndex((x) => {
      return x?.liquidityToken?.equals(pair.liquidityToken)
    }) !== -1
  })

  return (
    <>
      <AppBody>
        <SwapPoolTabs active={'pool'}/>
        <AutoColumn gap="lg" justify="center">
          <ButtonPrimary id="join-pool-button" as={Link} style={{ padding: 16 }} to={'/add/' + ZERO_ADDRESS
          }>
            <Text fontWeight={500} fontSize={20}>
              Add Liquidity
            </Text>
          </ButtonPrimary>

          <AutoColumn gap="12px" style={{ width: '100%' }}>
            <RowBetween padding={'0 8px'}>
              <Text color={theme.text1} fontWeight={500}>
                Your Liquidity
              </Text>
              <Question text="When you add liquidity, you are given pool tokens that represent your share. If you don’t see a pool you joined in this list, try importing a pool below."/>
            </RowBetween>

            {!account ? (
              <LightCard padding="40px">
                <TYPE.body color={theme.text3} textAlign="center">
                  Connect to a wallet to view your liquidity.
                </TYPE.body>
              </LightCard>
            ) : v2IsLoading ? (
              <LightCard padding="40px">
                <TYPE.body color={theme.text3} textAlign="center">
                  <Dots>Loading</Dots>
                </TYPE.body>
              </LightCard>
            ) : allV2PairsWithLiquidity?.length > 0 ? (
              <>
                {allV2PairsWithLiquidity.map(v2Pair => (
                  <FullPositionCard key={v2Pair.liquidityToken.address} pair={v2Pair}/>
                ))}
              </>
            ) : (
              <LightCard padding="40px">
                <TYPE.body color={theme.text3} textAlign="center">
                  No liquidity found.
                </TYPE.body>
              </LightCard>
            )}

            <div>
              <Text textAlign="center" fontSize={14} style={{ padding: '.5rem 0 .5rem 0' }}>
                {'Don\'t see a pool you joined?'}{' '}
                <StyledInternalLink id="import-pool-link" to={false ? '/migrate' : '/find'}>
                  {'Import it.'}
                </StyledInternalLink>
              </Text>

              {account ? <ReferralLink/> : ('')}
            </div>
          </AutoColumn>
        </AutoColumn>
      </AppBody>

      <div style={{ display: 'flex', alignItems: 'center', marginTop: '1.5rem' }}>
        <ButtonSecondary as={Link} style={{ width: 'initial' }} to="/migrate">
          Migrate Liquidity to Mooniswap
        </ButtonSecondary>
      </div>
    </>
  )
}
